import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hasAcceptedCurrentTerms, formatAcceptedAt } from '../legal/terms';
import { Link, useNavigate } from 'react-router-dom';
import ServerDiscovery from '../services/ServerDiscovery';
import { SeoHelmet } from '../hooks/useSeo';
import {
  authHeaders,
  getToken,
  getUser,
  setSession,
  signOut,
  subscribeAuth,
} from '../utils/auth';
import AuthPanel from './AuthPanel';
import { trackEvent } from '../utils/analytics';
import AccountButton from './AccountButton';
import BackButton from './BackButton';
import { GuessRow } from '../langle/ladder';

const ARCHIVE_START = '2026-04-26';
// Easy/Hard launched 2026-09-10 (must match backend's DIFFICULTY_START in
// backend/utils/daily.py) — Classic's history goes back further.
const DIFFICULTY_LAUNCH = '2026-09-10';
const HISTORY_TABS = [
  { value: 'easy', label: 'Easy' },
  { value: 'hard', label: 'Hard' },
  { value: 'classic', label: 'Classic' },
];

function VerifyEmailBanner({ httpBase }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const resend = async () => {
    if (!httpBase || busy) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`${httpBase}/auth/resend-verification`, {
        method: 'POST',
        headers: { ...authHeaders() },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not resend');
      }
      setMessage(data.message || 'Verification email sent');
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="lng-verify-banner">
      <p>
        Confirm your email so you can reset your password later.
        Check your inbox for a link from LangShazam.
      </p>
      <button type="button" className="lng-secondary-btn" onClick={resend} disabled={busy || !httpBase}>
        {busy ? 'Sending…' : 'Resend verification email'}
      </button>
      {message && <p className="lng-verify-hint">{message}</p>}
      {error && <p className="lng-inline-error">{error}</p>}
    </section>
  );
}

function DailyEmailPref({ httpBase, user, onUser }) {
  const optedIn = Boolean(user?.dailyEmailOptIn);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const toggle = async () => {
    if (!httpBase || busy) return;
    const next = !optedIn;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${httpBase}/auth/preferences`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        body: JSON.stringify({ dailyEmailOptIn: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not update preference');
      }
      const updated = data.user || { ...user, dailyEmailOptIn: next };
      onUser(updated);
      if (getToken()) setSession(getToken(), updated);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lng-email-pref">
      <label className="lng-email-pref-row">
        <input
          type="checkbox"
          checked={optedIn}
          onChange={toggle}
          disabled={busy || !httpBase}
        />
        <span>Daily Langle email{optedIn ? '' : ' (off)'}</span>
      </label>
      {error && <p className="lng-inline-error">{error}</p>}
    </div>
  );
}

function monthKey(year, month) {
  return year * 12 + month;
}

function compareMonths(a, b) {
  return monthKey(a.year, a.month) - monthKey(b.year, b.month);
}

function parseMonth(isoDate) {
  const [year, month] = isoDate.split('-').map(Number);
  return { year, month: month - 1 };
}
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function utcToday() {
  return new Date().toISOString().slice(0, 10);
}

function prettyDate(iso) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function monthLabel(year, month) {
  return new Date(Date.UTC(year, month, 1)).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

function mondayIndex(year, month) {
  const dow = new Date(Date.UTC(year, month, 1)).getUTCDay();
  return (dow + 6) % 7;
}

function loadLocalGuesses(puzzleId, difficulty) {
  // Mirrors Daily.js's stateKey(): classic keeps the bare key, easy/hard
  // suffix it — see that file for why (predates difficulty entirely).
  const suffix = difficulty && difficulty !== 'classic' ? `.${difficulty}` : '';
  try {
    const raw = localStorage.getItem(`langle.state.${puzzleId}${suffix}`);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.puzzleId === puzzleId ? (parsed.guesses || []) : [];
  } catch {
    return [];
  }
}

function PlayIcon() {
  return <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>;
}
function PauseIcon() {
  return <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>;
}
function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function useInfiniteScroll(onLoadMore, hasMore, loading) {
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!hasMore || loading) return undefined;
    const el = sentinelRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { rootMargin: '240px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore, loading]);
  return sentinelRef;
}

function DaySheet({ httpBase, puzzleId, play, difficulty, onClose }) {
  const [archive, setArchive] = useState(null);
  const [error, setError] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = difficulty && difficulty !== 'classic' ? `?difficulty=${difficulty}` : '';
        const res = await fetch(`${httpBase}/daily/archive/${puzzleId}${qs}`, {
          headers: authHeaders(),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || 'Could not load this day');
        if (!cancelled) setArchive(data);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load this day');
      }
    })();
    return () => { cancelled = true; };
  }, [httpBase, puzzleId, difficulty]);

  const guesses = archive?.play?.guesses?.length
    ? archive.play.guesses
    : (play?.guesses?.length ? play.guesses : loadLocalGuesses(puzzleId, difficulty));
  const result = archive?.play || play;
  const reveal = archive?.reveal;
  const audioSrc = archive ? `${httpBase}${archive.audioUrl}` : '';

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) el.play();
    else el.pause();
  };

  return (
    <div className="lng-modal-overlay" onClick={onClose}>
      <div className="lng-modal lng-day-sheet" onClick={(e) => e.stopPropagation()}>
        <button className="lng-modal-close" onClick={onClose} aria-label="Close"><CloseIcon /></button>
        <p className="lng-hero-date">{prettyDate(puzzleId)}</p>
        <h2 className="lng-modal-title">
          {result
            ? (result.won ? `Solved in ${result.guessCount}` : 'Missed')
            : (archive?.isToday ? "Today's puzzle" : 'Archive clip')}
        </h2>

        {error && <p className="lng-inline-error">{error}</p>}

        {archive && (
          <section className={`lng-audio-card${isPlaying ? ' is-playing' : ''}`}>
            <button className="lng-audio-btn" onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}>
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>
            <div className="lng-audio-bars" aria-hidden>
              {Array.from({ length: 22 }).map((_, i) => (
                <span key={i} style={{ animationDelay: `${(i % 7) * 0.07}s` }} />
              ))}
            </div>
            <div className="lng-audio-meta">
              <span className="lng-audio-label">{archive.isToday ? "Today's clip" : 'That day’s clip'}</span>
              <span className="lng-audio-sub">Unlimited replays</span>
            </div>
            <audio
              ref={audioRef}
              src={audioSrc}
              preload="auto"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
            />
          </section>
        )}

        {guesses.length > 0 && (
          <section className="lng-board lng-board-compact">
            {guesses.map((g, i) => (
              <GuessRow key={i} guess={g} />
            ))}
          </section>
        )}

        {reveal && (
          <div className="lng-reveal lng-reveal-compact">
            <div className="lng-reveal-name">
              <div className="lng-reveal-english">{reveal.name}</div>
              {reveal.nativeName && reveal.nativeName !== reveal.name && (
                <div className="lng-reveal-native">{reveal.nativeName}</div>
              )}
            </div>
            <p className="lng-day-meta">
              {[reveal.family, reveal.region].filter(Boolean).join(' · ')}
            </p>
            {reveal.funFact && <p className="lng-day-fact">{reveal.funFact}</p>}
          </div>
        )}

        {archive?.isToday && !result && (
          <Link to={`/langle/${difficulty || 'classic'}`} className="lng-share-btn lng-share-primary" style={{ width: '100%', textAlign: 'center' }}>
            Play today’s Langle
          </Link>
        )}
      </div>
    </div>
  );
}

function relativeWhen(ts) {
  const diff = Math.max(0, Math.floor(Date.now() / 1000) - ts);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(ts * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function detectionTitle(detection) {
  if (detection.outcome === 'detected' && detection.language) return detection.language;
  if (detection.outcome === 'no_detection') return 'No clear match';
  if (detection.outcome === 'timeout') return 'Timed out';
  if (detection.outcome === 'error') return 'Something went wrong';
  return 'Mic session';
}

/**
 * Delete-account flow: explains exactly what is erased, requires typing
 * DELETE, then calls POST /auth/delete-account and signs the browser out.
 */
function DeleteAccountPanel({ httpBase }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const ready = confirm.trim().toUpperCase() === 'DELETE';

  const run = async () => {
    if (!ready || busy || !httpBase) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${httpBase}/auth/delete-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ confirm: confirm.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not delete your account');
      }
      trackEvent('account_deleted');
      signOut();
      try { localStorage.removeItem('langle.stats'); } catch { /* ignore */ }
      navigate('/', { replace: true });
    } catch (e) {
      setError(e.message || String(e));
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" className="lng-text-btn lng-footer-link" onClick={() => setOpen(true)}>
        Delete account
      </button>

      {open && (
        <div className="modal-overlay" onClick={() => !busy && setOpen(false)}>
          <div className="modal-card gate-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h2 className="modal-title">Delete your account?</h2>
            <p className="modal-subtitle">This erases, immediately and permanently:</p>
            <ul className="lng-danger-list">
              <li>Your account: email, name, Google link, password, email preferences.</li>
              <li>Every detection session, including the audio recordings and transcripts.</li>
              <li>Your Langle history, streaks, and group memberships.</li>
            </ul>
            <p className="legal-status">
              We keep a record that you accepted the Terms of Service (version and time only, no
              personal details), and anonymous global game statistics are unaffected.
            </p>
            <label className="lng-danger-confirm">
              Type <strong>DELETE</strong> to confirm
              <input
                className="lng-input"
                type="text"
                autoComplete="off"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={busy}
                placeholder="DELETE"
              />
            </label>
            {error && <p className="lng-inline-error">{error}</p>}
            <button type="button" className="lng-google-btn lng-email-btn lng-danger-btn" onClick={run} disabled={!ready || busy}>
              {busy ? 'Deleting…' : 'Delete my account permanently'}
            </button>
            <button type="button" className="modal-btn" onClick={() => setOpen(false)} disabled={busy}>
              Keep my account
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function DetectionCard({ httpBase, detection, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioSrc, setAudioSrc] = useState('');
  const [audioError, setAudioError] = useState('');
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const audioRef = useRef(null);

  const transcript = detection.transcript || '';
  const preview = detection.transcriptPreview || transcript;
  const showToggle = transcript.length > (preview?.length || 0);

  const ensureAudio = async () => {
    if (audioSrc || !detection.hasAudio) return audioSrc;
    setLoadingAudio(true);
    setAudioError('');
    try {
      const res = await fetch(`${httpBase}/auth/detections/${detection.detectionId}/audio`, {
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Could not load audio');
      setAudioSrc(data.audioUrl);
      return data.audioUrl;
    } catch (e) {
      setAudioError(e.message || 'Could not load audio');
      return '';
    } finally {
      setLoadingAudio(false);
    }
  };

  const togglePlay = async () => {
    const el = audioRef.current;
    if (!el || !detection.hasAudio) return;
    if (el.paused) {
      const src = await ensureAudio();
      if (!src) return;
      if (el.src !== src) el.src = src;
      el.play();
    } else {
      el.pause();
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await onDelete(detection.detectionId);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <article className={`lng-det-card lng-det-${detection.outcome}`}>
      <div className="lng-det-head">
        <div>
          <p className="lng-det-kicker">LangShazam</p>
          <h4 className="lng-det-title">{detectionTitle(detection)}</h4>
        </div>
        <div className="lng-det-actions">
          <time className="lng-det-when" dateTime={new Date(detection.ts * 1000).toISOString()}>
            {relativeWhen(detection.ts)}
          </time>
          <button
            type="button"
            className="lng-det-delete"
            onClick={handleDelete}
            disabled={deleting}
            aria-label="Delete recording"
          >
            <TrashIcon />
          </button>
        </div>
      </div>

      {preview && (
        <blockquote className="lng-det-quote">
          <p>{expanded ? transcript : preview}</p>
          {showToggle && (
            <button type="button" className="lng-det-expand" onClick={() => setExpanded((v) => !v)}>
              {expanded ? 'Show less' : 'Full transcript'}
            </button>
          )}
        </blockquote>
      )}

      {detection.hasAudio && (
        <section className={`lng-audio-card lng-det-audio${isPlaying ? ' is-playing' : ''}`}>
          <button
            type="button"
            className="lng-audio-btn"
            onClick={togglePlay}
            disabled={loadingAudio}
            aria-label={isPlaying ? 'Pause recording' : 'Play recording'}
          >
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>
          <div className="lng-audio-bars" aria-hidden>
            {Array.from({ length: 16 }).map((_, i) => (
              <span key={i} style={{ animationDelay: `${(i % 5) * 0.08}s` }} />
            ))}
          </div>
          <div className="lng-audio-meta">
            <span className="lng-audio-label">Your recording</span>
            <span className="lng-audio-sub">{loadingAudio ? 'Loading…' : 'Replay anytime'}</span>
          </div>
          <audio
            ref={audioRef}
            preload="none"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
          />
        </section>
      )}

      {audioError && <p className="lng-inline-error lng-det-audio-error">{audioError}</p>}
    </article>
  );
}

function Account() {
  const [user, setUser] = useState(getUser);
  const [httpBase, setHttpBase] = useState(null);
  const [stats, setStats] = useState(null);
  const [plays, setPlays] = useState([]);
  const [detections, setDetections] = useState([]);
  const [detectionCount, setDetectionCount] = useState(0);
  const [detectionsHasMore, setDetectionsHasMore] = useState(false);
  const [detectionsLoading, setDetectionsLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupCount, setGroupCount] = useState(0);
  const [groupsHasMore, setGroupsHasMore] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [retryKey, setRetryKey] = useState(0);
  const [openDay, setOpenDay] = useState(null);
  const [difficulty, setDifficulty] = useState('easy');
  const detectionsBusyRef = useRef(false);
  const groupsBusyRef = useRef(false);
  const httpBaseRef = useRef(null);
  httpBaseRef.current = httpBase;
  const today = utcToday();
  const todayDate = new Date(`${today}T00:00:00Z`);
  const [cursor, setCursor] = useState({
    year: todayDate.getUTCFullYear(),
    month: todayDate.getUTCMonth(),
  });

  useEffect(() => subscribeAuth(setUser), []);

  const loadHistory = useCallback(async (signal) => {
    if (!getToken()) {
      setLoading(false);
      setStats(null);
      setPlays([]);
      setDetections([]);
      setDetectionCount(0);
      setDetectionsHasMore(false);
      setGroups([]);
      setGroupsHasMore(false);
      setGroupCount(0);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const base = await ServerDiscovery.discoverHttpBase();
      setHttpBase(base);
      const qs = difficulty !== 'classic' ? `?difficulty=${difficulty}` : '';
      const res = await fetch(`${base}/auth/history${qs}`, { headers: authHeaders(), signal });
      if (res.status === 401) {
        signOut();
        setUser(null);
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Could not load account (${res.status})`);
      setUser(data.user);
      if (data.user && getToken()) setSession(getToken(), data.user);
      setStats(data.stats);
      setPlays(data.plays || []);
      setDetectionCount(data.detectionCount ?? 0);
      setDetections(data.detections || []);
      setDetectionsHasMore(Boolean(data.detectionsHasMore));
      setGroups(data.groups || []);
      setGroupsHasMore(Boolean(data.groupsHasMore));
      setGroupCount(data.groupCount ?? (data.groups || []).length);
    } catch (e) {
      if (e.name === 'AbortError') return;
      const msg = e.message || 'Could not load account';
      setError(msg === 'Failed to fetch'
        ? 'Could not reach the server. Check your connection and try again.'
        : msg);
    } finally {
      setLoading(false);
    }
  }, [difficulty]);

  const loadMoreDetections = useCallback(async () => {
    const base = httpBaseRef.current;
    if (!base || detectionsBusyRef.current || !detectionsHasMore) return;
    const beforeTs = detectionsRef.current.at(-1)?.ts;
    if (beforeTs == null) return;

    detectionsBusyRef.current = true;
    setDetectionsLoading(true);
    try {
      const res = await fetch(`${base}/auth/detections?before_ts=${beforeTs}`, { headers: authHeaders() });
      if (res.status === 401) {
        signOut();
        setUser(null);
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Could not load more detections');
      setDetections((prev) => [...prev, ...(data.detections || [])]);
      setDetectionsHasMore(Boolean(data.hasMore));
    } catch (e) {
      setError(e.message || 'Could not load more detections');
    } finally {
      detectionsBusyRef.current = false;
      setDetectionsLoading(false);
    }
  }, [detectionsHasMore]);

  const loadMoreGroups = useCallback(async () => {
    const base = httpBaseRef.current;
    if (!base || groupsBusyRef.current || !groupsHasMore) return;
    const beforeJoinedAt = groupsRef.current.at(-1)?.joinedAt;
    if (beforeJoinedAt == null) return;

    groupsBusyRef.current = true;
    setGroupsLoading(true);
    try {
      const res = await fetch(`${base}/auth/groups?before_joined_at=${beforeJoinedAt}`, { headers: authHeaders() });
      if (res.status === 401) {
        signOut();
        setUser(null);
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Could not load more groups');
      setGroups((prev) => [...prev, ...(data.groups || [])]);
      setGroupsHasMore(Boolean(data.hasMore));
    } catch (e) {
      setError(e.message || 'Could not load more groups');
    } finally {
      groupsBusyRef.current = false;
      setGroupsLoading(false);
    }
  }, [groupsHasMore]);

  const deleteDetection = useCallback(async (detectionId) => {
    if (!httpBase) return;
    const res = await fetch(`${httpBase}/auth/detections/${detectionId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || 'Could not delete recording');
    }
    setDetections((prev) => prev.filter((d) => d.detectionId !== detectionId));
    setDetectionCount((c) => Math.max(0, c - 1));
  }, [httpBase]);

  useEffect(() => {
    if (!getToken()) {
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    loadHistory(controller.signal);
    return () => controller.abort();
  // Re-run when the signed-in user changes too: creating an account in the
  // AuthPanel on this very page used to leave httpBase unset (the effect had
  // only run signed-out), so every action needing the server stayed disabled
  // until a reload.
  }, [loadHistory, retryKey, user?.userId]);

  const detectionsRef = useRef([]);
  detectionsRef.current = detections;
  const groupsRef = useRef([]);
  groupsRef.current = groups;

  const detectionsSentinelRef = useInfiniteScroll(
    loadMoreDetections,
    detectionsHasMore,
    detectionsLoading || loading,
  );

  const groupsRemaining = Math.max(0, groupCount - groups.length);

  const playById = useMemo(() => {
    const map = {};
    for (const play of plays) map[play.puzzleId] = play;
    return map;
  }, [plays]);

  // Easy/Hard have no history before their launch date, regardless of
  // Classic's much longer archive.
  const archiveStart = difficulty === 'classic' ? ARCHIVE_START : DIFFICULTY_LAUNCH;
  const archiveMonth = useMemo(() => parseMonth(archiveStart), [archiveStart]);
  const currentMonth = useMemo(
    () => ({ year: todayDate.getUTCFullYear(), month: todayDate.getUTCMonth() }),
    [todayDate],
  );
  const earliestMonth = useMemo(() => {
    let earliest = archiveMonth;
    for (const play of plays) {
      const month = parseMonth(play.puzzleId);
      if (compareMonths(month, earliest) < 0) earliest = month;
    }
    return earliest;
  }, [plays, archiveMonth]);

  const cells = useMemo(() => {
    const { year, month } = cursor;
    const lead = mondayIndex(year, month);
    const count = daysInMonth(year, month);
    const out = [];
    for (let i = 0; i < lead; i += 1) out.push(null);
    for (let day = 1; day <= count; day += 1) {
      const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      out.push(iso);
    }
    return out;
  }, [cursor]);

  const canPrev = compareMonths(cursor, earliestMonth) > 0;
  const canNext = compareMonths(cursor, currentMonth) < 0;

  const shiftMonth = (delta) => {
    setCursor((prev) => {
      const date = new Date(Date.UTC(prev.year, prev.month + delta, 1));
      return { year: date.getUTCFullYear(), month: date.getUTCMonth() };
    });
  };

  const winRate = stats?.played ? Math.round((stats.wins / stats.played) * 100) : 0;
  const maxBar = Math.max(1, ...(stats?.distribution || [0]));
  const openPlay = openDay ? playById[openDay] : null;

  return (
    <div className="lng-page">
      <SeoHelmet
        title="Your LangShazam account | LangShazam"
        description="Your Langle stats, LangShazam detections, past puzzles, and replayable audio."
        path="/account"
      />
      <header className="lng-topbar">
        <BackButton fallback="/" label="Back" />
        <h1 className="lng-brand">Account</h1>
        <div className="lng-topbar-actions">
          <AccountButton />
        </div>
      </header>

      {!user && (
        <section className="lng-account-gate">
          <AuthPanel nextPath="/account" />
          <p className="lng-afterthought"><Link to="/langle">Play today’s puzzle</Link></p>
        </section>
      )}

      {user && (
        <>
          <section className="lng-profile-card">
            {user.picture ? (
              <img className="lng-profile-photo" src={user.picture} alt="" referrerPolicy="no-referrer" />
            ) : (
              <div className="lng-profile-photo lng-profile-fallback">{(user.name || '?')[0]}</div>
            )}
            <div className="lng-profile-copy">
              <h2>{user.name || 'Player'}</h2>
              <p>{user.email}</p>
            </div>
            <button type="button" className="lng-secondary-btn" onClick={signOut}>Sign out</button>
          </section>

          {user.emailVerified === false && (
            <VerifyEmailBanner httpBase={httpBase} />
          )}

          {error && (
            <div className="lng-inline-error lng-account-error">
              <p>{error}</p>
              <button type="button" className="lng-secondary-btn" onClick={() => setRetryKey((k) => k + 1)}>
                Try again
              </button>
            </div>
          )}
          {loading && <p className="lng-loading">Loading your games…</p>}

          {stats && (
            <>
              <div className="lng-stats-row">
                <div><strong>{stats.played}</strong><span>Played</span></div>
                <div><strong>{winRate}</strong><span>Win %</span></div>
                <div><strong>{stats.currentStreak}</strong><span>Streak</span></div>
                <div><strong>{stats.maxStreak}</strong><span>Best</span></div>
              </div>

              {stats.played > 0 && (
                <>
                  <h3 className="lng-modal-subtitle">Guess distribution</h3>
                  <div className="lng-histogram">
                    {stats.distribution.map((count, i) => (
                      <div className="lng-histogram-row" key={i}>
                        <span className="lng-histogram-label">{i + 1}</span>
                        <div className="lng-histogram-bar-wrap">
                          <div
                            className={`lng-histogram-bar${count === 0 ? ' is-empty' : ''}`}
                            style={{ width: `${Math.max(8, (count / maxBar) * 100)}%` }}
                          >
                            <span>{count}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          <section className="lng-account-panel lng-groups-section">
            <div className="lng-section-head">
              <h3 className="lng-modal-subtitle">Groups</h3>
              {groupCount > 0 && (
                <span className="lng-section-badge">{groupCount}</span>
              )}
            </div>
            {groupCount > 0 && groups.length < groupCount && (
              <p className="lng-cal-hint">
                Showing {groups.length} of {groupCount} groups.
              </p>
            )}
            {groups.length === 0 && !groupsLoading && (
              <p className="lng-cal-hint">No groups yet. Create one from Langle to play with friends.</p>
            )}
            <ul className="lng-group-grid">
              {groups.map((group) => (
                <li key={group.groupId}>
                  <Link to={`/group/${group.groupId}`} className="lng-group-card">
                    <span className="lng-group-name">{group.name}</span>
                    <span className="lng-group-arrow" aria-hidden>→</span>
                  </Link>
                </li>
              ))}
            </ul>
            {groupsHasMore && (
              <button
                type="button"
                className="lng-secondary-btn lng-load-more-btn"
                onClick={loadMoreGroups}
                disabled={groupsLoading}
              >
                {groupsLoading
                  ? 'Loading…'
                  : `Load more groups${groupsRemaining > 0 ? ` (${Math.min(8, groupsRemaining)} more)` : ''}`}
              </button>
            )}
          </section>

          <section className="lng-account-panel lng-detections-section">
            <div className="lng-section-head">
              <h3 className="lng-modal-subtitle">LangShazam detections</h3>
              <span className="lng-section-badge">{detectionCount}</span>
            </div>
            <p className="lng-cal-hint">
              Mic sessions saved while you’re signed in — your guess, transcript, and recording.
            </p>
            {detections.length === 0 && !detectionsLoading && (
              <div className="lng-det-empty">
                <p>No saved detections yet.</p>
                <Link to="/">Try the mic on the home page</Link>
              </div>
            )}
            <div className="lng-det-grid">
              {httpBase && detections.map((detection) => (
                <DetectionCard
                  key={detection.detectionId}
                  httpBase={httpBase}
                  detection={detection}
                  onDelete={deleteDetection}
                />
              ))}
            </div>
            {detectionsLoading && detections.length > 0 && (
              <p className="lng-panel-loading">Loading more…</p>
            )}
            {detectionsLoading && detections.length === 0 && (
              <p className="lng-panel-loading">Loading detections…</p>
            )}
            <div ref={detectionsSentinelRef} className="lng-scroll-sentinel" aria-hidden />
          </section>

          <section className="lng-account-section">
            <nav className="lng-difficulty-tabs" aria-label="Difficulty">
              {HISTORY_TABS.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  className={`lng-difficulty-tab${tab.value === difficulty ? ' is-active' : ''}`}
                  onClick={() => {
                    setDifficulty(tab.value);
                    setCursor({ year: todayDate.getUTCFullYear(), month: todayDate.getUTCMonth() });
                    setOpenDay(null);
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
            <div className="lng-cal-head">
              <button type="button" className="lng-icon-btn" onClick={() => shiftMonth(-1)} disabled={!canPrev} aria-label="Previous month">‹</button>
              <h3 className="lng-modal-subtitle">{monthLabel(cursor.year, cursor.month)}</h3>
              <button type="button" className="lng-icon-btn" onClick={() => shiftMonth(1)} disabled={!canNext} aria-label="Next month">›</button>
            </div>
            <p className="lng-cal-hint">
              Tap a day to hear that clip. Green is a win, gray is a miss.
              {(canPrev || canNext) && ' Use ‹ › to browse past months.'}
            </p>
            <div className="lng-cal">
              {WEEKDAYS.map((d) => <span key={d} className="lng-cal-dow">{d}</span>)}
              {cells.map((iso, i) => {
                if (!iso) return <span key={`e${i}`} className="lng-cal-cell is-empty" />;
                const play = playById[iso];
                const future = iso > today;
                const before = iso < archiveStart;
                const cls = [
                  'lng-cal-cell',
                  iso === today ? 'is-today' : '',
                  play?.won ? 'is-win' : '',
                  play && !play.won ? 'is-loss' : '',
                  (future || before) ? 'is-disabled' : '',
                ].filter(Boolean).join(' ');
                return (
                  <button
                    key={iso}
                    type="button"
                    className={cls}
                    disabled={future || before}
                    onClick={() => setOpenDay(iso)}
                    aria-label={prettyDate(iso)}
                  >
                    {Number(iso.slice(-2))}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="lng-account-section">
            <h3 className="lng-modal-subtitle">Your games</h3>
            {plays.length === 0 && !loading && (
              <p className="lng-cal-hint">
                No saved games yet. <Link to={`/langle/${difficulty}`}>Play today</Link>, then they show up here.
              </p>
            )}
            <ul className="lng-account-list">
              {plays.map((play) => (
                <li key={play.puzzleId}>
                  <button type="button" className="lng-history-row" onClick={() => setOpenDay(play.puzzleId)}>
                    <span>{prettyDate(play.puzzleId)}</span>
                    <strong>{play.won ? `${play.guessCount}/6` : 'X/6'}</strong>
                  </button>
                </li>
              ))}
            </ul>
          </section>

          <DailyEmailPref httpBase={httpBase} user={user} onUser={setUser} />

          <p className="lng-afterthought">
            <Link to="/">Back to LangShazam</Link>
            {' · '}
            <Link to={`/langle/${difficulty}`}>Play Langle</Link>
          </p>

          {/* Legal footer: the quiet place for terms, privacy, and the
              account-deletion link, the way most services tuck these away. */}
          <div className="lng-account-footer">
            <Link to="/terms">Terms</Link>
            {' · '}
            <Link to="/privacy">Privacy</Link>
            {hasAcceptedCurrentTerms(user) && formatAcceptedAt(user.termsAcceptedAt) && (
              <>
                {' · '}
                <span title={`Terms of Service version ${user.termsVersion}`}>
                  Accepted {formatAcceptedAt(user.termsAcceptedAt)}
                </span>
              </>
            )}
            {' · '}
            <DeleteAccountPanel httpBase={httpBase} />
          </div>
        </>
      )}

      {openDay && httpBase && (
        <DaySheet
          httpBase={httpBase}
          puzzleId={openDay}
          play={openPlay}
          difficulty={difficulty}
          onClose={() => setOpenDay(null)}
        />
      )}
    </div>
  );
}

export default Account;
